<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import VacancyDialog from '@/views/recruiting/VacancyDialog.vue'
import { Modules } from '@/types/generated'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'
import VacancyData = Modules.Recruiting.Data.VacancyData
import VacancyStatusEnum = Modules.Recruiting.Enums.VacancyStatusEnum

definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

interface ScenarioOption {
  id: string
  title: string
}

const { t } = useI18n()
const { locale } = useI18n({ useScope: 'global' })
const router = useRouter()
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

// Тот же снапшот фичи, что гейтит пункт меню (см. `DefaultLayoutWithVerticalNav`). Это не
// защита — бэкенд на выключенной фиче и так отвечает 403, — а чтобы прямой заход по адресу
// не показывал пустой раздел.
const orgSettings = useCookie<any>('organizationSettings')
if (!(orgSettings.value?.enabledFeatures ?? []).includes('recruiting'))
  await router.replace('/games/list')

const vacancies = ref<VacancyData[]>([])
const scenarios = ref<ScenarioOption[]>([])
const isLoading = ref(true)
const loadFailed = ref(false)
const loadErrorMessage = ref('')
const searchQuery = ref('')

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isEditDialogVisible = ref(false)
const isConfirmArchiveVisible = ref(false)
const activeVacancy = ref<VacancyData | null>(null)

const headers = [
  { title: t('recruiting-page.headers.title'), key: 'title' },
  { title: t('recruiting-page.headers.scenario'), key: 'scenario', sortable: false },
  { title: t('recruiting-page.headers.status'), key: 'status' },
  { title: t('recruiting-page.headers.expires_at'), key: 'expiresAt' },
  { title: t('recruiting-page.headers.actions'), key: 'actions', sortable: false },
]

const notify = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

// Продуктовые отказы модуля — 422 с машинным `code` и готовым текстом в `message`; его и
// показываем. Всё остальное (403 выключенной фичи, 5xx) отдаёт служебные тексты Laravel —
// их пользователю показывать нечего, отсюда `fallback` вместо них.
const refusalText = (error: any, fallback: string = t('recruiting-page.messages.error')): string => {
  const status = error?.statusCode ?? error?.response?.status

  if (status !== 422)
    return fallback

  return error?.data?.message ?? error?.data?.error ?? fallback
}

const scenarioTitleById = computed(() => {
  const map = new Map<string, string>()

  for (const scenario of scenarios.value)
    map.set(String(scenario.id), scenario.title)

  return map
})

const scenarioTitle = (vacancy: VacancyData): string =>
  scenarioTitleById.value.get(String(vacancy.scenarioId)) ?? '—'

const statusLabel = (status: string): string => t(`recruiting-page.status.${status}`)

const statusColor = (status: string): string => {
  if (status === VacancyStatusEnum.Active)
    return 'success'
  if (status === VacancyStatusEnum.Archived)
    return 'secondary'

  return 'warning'
}

const isArchived = (vacancy: VacancyData): boolean => vacancy.status === VacancyStatusEnum.Archived

const expiresLabel = (vacancy: VacancyData): string => vacancy.expiresAt
  ? formatDateToMonthShort(vacancy.expiresAt, false, dateLocale, userTimeZone.value)
  : t('recruiting-page.no_expiry')

const filteredVacancies = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  if (!query)
    return vacancies.value

  return vacancies.value.filter(vacancy => vacancy.title.toLowerCase().includes(query))
})

const fetchVacancies = async () => {
  isLoading.value = true
  loadFailed.value = false
  try {
    vacancies.value = (await $api<VacancyData[]>('/recruiting/vacancies')) ?? []
  }
  catch (error: any) {
    vacancies.value = []
    loadFailed.value = true
    loadErrorMessage.value = refusalText(error, t('recruiting-page.load_error'))
  }
  finally {
    isLoading.value = false
  }
}

const fetchScenarios = async () => {
  try {
    scenarios.value = (await $api<ScenarioOption[]>('/scenarios/all-for-ai')) ?? []
  }
  catch {
    scenarios.value = []
  }
}

const openCreate = () => {
  activeVacancy.value = null
  isEditDialogVisible.value = true
}

const openEdit = (vacancy: VacancyData) => {
  activeVacancy.value = vacancy
  isEditDialogVisible.value = true
}

const onSaved = async () => {
  notify(activeVacancy.value ? t('recruiting-page.messages.updated') : t('recruiting-page.messages.created'))
  await fetchVacancies()
}

const askArchive = (vacancy: VacancyData) => {
  activeVacancy.value = vacancy
  isConfirmArchiveVisible.value = true
}

// Обновление вакансии — PUT с полным телом, поэтому «в архив» переотправляет все поля,
// включая внешние идентификаторы ATS: иначе они бы обнулились.
//
// `expiresAt` уходит ровно тем, чем пришло, — ISO-строкой со смещением. Это и есть контракт
// срока в подборе (`views/recruiting/expiresAt.ts`): сервер читает момент времени, а не стенные
// часы, поэтому переотправка нетронутого значения не двигает срок и escape-hatch «прежнее
// сохранённое значение проходит» узнаёт его даже у вакансии с истёкшей датой.
const confirmArchive = async (confirmed: boolean) => {
  isConfirmArchiveVisible.value = false

  const vacancy = activeVacancy.value

  if (!confirmed || !vacancy)
    return

  // response._data.errors — тот же формат, что маппит `VacancyDialog`; здесь только поле
  // scenario_id важно, остальное поле уже провалидировано на клиенте раньше.
  let archiveErrorMessage = ''

  try {
    await $api(`/recruiting/vacancies/${vacancy.id}`, {
      method: 'PUT',
      body: {
        title: vacancy.title,
        scenario_id: String(vacancy.scenarioId),
        status: VacancyStatusEnum.Archived,
        expires_at: vacancy.expiresAt,
        external_source: vacancy.externalSource,
        external_id: vacancy.externalId,
      },
      onResponseError({ response }) {
        const scenarioError = response._data?.errors?.scenario_id

        archiveErrorMessage = (Array.isArray(scenarioError) ? scenarioError[0] : scenarioError)
          ?? response._data?.message
          ?? ''
      },
    })
    notify(t('recruiting-page.messages.archived'))
    await fetchVacancies()
  }
  catch {
    notify(archiveErrorMessage || t('recruiting-page.messages.error'), 'error')
  }
}

await Promise.all([fetchVacancies(), fetchScenarios()])
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4">
      <h4 class="text-h4">
        {{ t('recruiting-page.title') }}
      </h4>
      <div class="d-flex align-center flex-wrap gap-3">
        <VTextField
          v-model="searchQuery"
          clearable
          :placeholder="t('search')"
          style="min-inline-size: 200px;"
        />
        <VBtn
          prepend-icon="tabler-plus"
          @click="openCreate"
        >
          {{ t('recruiting-page.create') }}
        </VBtn>
      </div>
    </div>

    <VCard>
      <VAlert
        v-if="loadFailed"
        type="error"
        variant="tonal"
        class="ma-4"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <span>{{ loadErrorMessage }}</span>
          <VBtn
            size="small"
            variant="tonal"
            color="error"
            @click="fetchVacancies"
          >
            {{ t('recruiting-page.retry') }}
          </VBtn>
        </div>
      </VAlert>

      <VDataTable
        v-else
        :headers="headers"
        :items="filteredVacancies"
        :loading="isLoading"
        :loading-text="t('loading')"
        :items-per-page="25"
        class="text-no-wrap"
      >
        <template #item.title="{ item }">
          <RouterLink
            :to="{ name: 'recruiting-id', params: { id: String(item.id) } }"
            class="font-weight-medium text-link"
          >
            {{ item.title }}
          </RouterLink>
        </template>

        <template #item.scenario="{ item }">
          {{ scenarioTitle(item) }}
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="statusColor(item.status)"
            size="small"
          >
            {{ statusLabel(item.status) }}
          </VChip>
        </template>

        <template #item.expiresAt="{ item }">
          {{ expiresLabel(item) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn :to="{ name: 'recruiting-id', params: { id: String(item.id) } }">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.open') }}
              </VTooltip>
              <VIcon icon="tabler-users" />
            </IconBtn>
            <IconBtn @click="openEdit(item)">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.edit') }}
              </VTooltip>
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              v-if="!isArchived(item)"
              @click="askArchive(item)"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('recruiting-page.archive') }}
              </VTooltip>
              <VIcon icon="tabler-archive" />
            </IconBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-disabled py-6">
            {{ t('recruiting-page.empty') }}
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VacancyDialog
      v-model:is-dialog-visible="isEditDialogVisible"
      :vacancy="activeVacancy"
      :scenarios="scenarios"
      @submit="onSaved"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmArchiveVisible"
      :confirmation-question="t('recruiting-page.confirm_archive')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmArchive"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
